import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import AppLayout from "../AppLayout";
import { API } from "../apiConfig";
import { Plus, Search, Edit, Trash2, Save, X, ArrowLeft } from "lucide-react";

const API_BASE = API;

const emptyForm = {
  supplier_name: "",
  mobile_no: "",
  gst_no: "",
  address: "",
  opening_balance: 0,
  is_active: 1,
};

function Supplier() {
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  useEffect(() => {
    loadSuppliers(1);
  }, []);

  const authHeader = () => ({
    headers: {
      Authorization: `Bearer ${sessionStorage.getItem("token") || ""}`,
    },
  });

  const loadSuppliers = async (page = 1) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/suppliers`, {
        ...authHeader(),
        params: {
          page,
          per_page: pagination.per_page,
          search,
        },
      });

      const data = res.data.data;

      setSuppliers(data.data || []);
      setPagination({
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      });
    } catch (error) {
      console.log(error);
      Swal.fire("Error", "Supplier loading failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const saveSupplier = async (e) => {
    e.preventDefault();

    if (!form.supplier_name.trim()) {
      Swal.fire("Required", "Please enter supplier name", "warning");
      return;
    }

    try {
      const payload = {
        supplier_name: form.supplier_name.trim(),
        mobile_no: form.mobile_no || null,
        gst_no: form.gst_no || null,
        address: form.address || null,
        opening_balance: Number(form.opening_balance || 0),
        is_active: Number(form.is_active),
      };

      if (editId) {
        await axios.put(`${API_BASE}/suppliers/${editId}`, payload, authHeader());
        Swal.fire("Updated", "Supplier updated successfully", "success");
      } else {
        await axios.post(`${API_BASE}/suppliers`, payload, authHeader());
        Swal.fire("Saved", "Supplier saved successfully", "success");
      }

      closeForm();
      loadSuppliers(pagination.current_page);
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Something went wrong while saving supplier";

      Swal.fire("Error", msg, "error");
    }
  };

  const editSupplier = (item) => {
    setEditId(item.id);
    setForm({
      supplier_name: item.supplier_name || "",
      mobile_no: item.mobile_no || "",
      gst_no: item.gst_no || "",
      address: item.address || "",
      opening_balance: item.opening_balance || 0,
      is_active: Number(item.is_active),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteSupplier = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Supplier?",
      text: "This supplier will be deleted permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE}/suppliers/${id}`, authHeader());
      Swal.fire("Deleted", "Supplier deleted successfully", "success");
      loadSuppliers(pagination.current_page);
    } catch (error) {
      Swal.fire("Error", "Supplier delete failed", "error");
    }
  };

  return (
    <AppLayout
      title={showForm ? (editId ? "Edit Supplier" : "Add Supplier") : "Supplier Master"}
      subtitle={
        showForm
          ? "Enter supplier details for purchases"
          : "Manage supplier details"
      }
    >
      {showForm ? (
        <div className="product-table-page">
          <div className="product-inline-card product-form-only-card">
            <div className="product-inline-header">
              <div>
                <h3>{editId ? "Update Supplier" : "Add Supplier"}</h3>
                <p>
                  {editId
                    ? "Update existing supplier information"
                    : "Create new supplier for purchases"}
                </p>
              </div>

              <button className="product-inline-close" onClick={closeForm}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={saveSupplier}>
              <div className="product-inline-grid">
                <div className="eb-field">
                  <label>Supplier Name *</label>
                  <input
                    name="supplier_name"
                    value={form.supplier_name}
                    onChange={handleChange}
                    placeholder="Enter supplier name"
                  />
                </div>

                <div className="eb-field">
                  <label>Mobile No</label>
                  <input
                    name="mobile_no"
                    value={form.mobile_no}
                    onChange={handleChange}
                    placeholder="Enter mobile number"
                  />
                </div>

                <div className="eb-field">
                  <label>GST No Optional</label>
                  <input
                    name="gst_no"
                    value={form.gst_no}
                    onChange={handleChange}
                    placeholder="Enter GST number"
                  />
                </div>

                <div className="eb-field">
                  <label>Opening Balance</label>
                  <input
                    type="number"
                    step="0.01"
                    name="opening_balance"
                    value={form.opening_balance}
                    onChange={handleChange}
                    placeholder="0.00"
                  />
                </div>

                <div className="eb-field">
                  <label>Status</label>
                  <select
                    name="is_active"
                    value={form.is_active}
                    onChange={handleChange}
                  >
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </select>
                </div>

                <div className="eb-field">
                  <label>Address</label>
                  <input
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="Enter address"
                  />
                </div>
              </div>

              <div className="product-inline-actions">
                <button
                  type="button"
                  className="product-cancel-btn"
                  onClick={closeForm}
                >
                  <ArrowLeft size={18} />
                  Back
                </button>

                <button type="submit" className="product-save-btn">
                  <Save size={18} />
                  {editId ? "Update Supplier" : "Save Supplier"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <div className="product-table-page">
          <div className="product-toolbar">
            <div className="product-search-box">
              <Search size={20} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") loadSuppliers(1);
                }}
                placeholder="Search suppliers"
              />
            </div>

            <button className="product-add-btn" onClick={openAddForm}>
              <Plus size={20} />
              ADD SUPPLIER
            </button>
          </div>

          <div className="product-table-card">
            <div className="product-table-wrapper">
              <table className="product-simple-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Supplier Name</th>
                    <th>Mobile No</th>
                    <th>Opening Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="product-empty-row">
                        Loading suppliers...
                      </td>
                    </tr>
                  ) : suppliers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="product-empty-row">
                        No suppliers found
                      </td>
                    </tr>
                  ) : (
                    suppliers.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>{item.supplier_name}</td>
                        <td>{item.mobile_no || "-"}</td>
                        <td>₹{Number(item.opening_balance || 0).toFixed(2)}</td>
                        <td>
                          <span
                            className={
                              Number(item.is_active)
                                ? "product-status-active"
                                : "product-status-inactive"
                            }
                          >
                            {Number(item.is_active) ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <button
                            className="product-action-btn edit"
                            onClick={() => editSupplier(item)}
                          >
                            <Edit size={22} />
                          </button>

                          <button
                            className="product-action-btn delete"
                            onClick={() => deleteSupplier(item.id)}
                          >
                            <Trash2 size={22} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="product-pagination">
              <span>
                {suppliers.length === 0
                  ? "0–0"
                  : `${(pagination.current_page - 1) * pagination.per_page + 1}–${
                      (pagination.current_page - 1) * pagination.per_page + suppliers.length
                    }`} of {pagination.total}
              </span>

              <button
                disabled={pagination.current_page <= 1}
                onClick={() => loadSuppliers(pagination.current_page - 1)}
              >
                ‹
              </button>

              <button
                disabled={pagination.current_page >= pagination.last_page}
                onClick={() => loadSuppliers(pagination.current_page + 1)}
              >
                ›
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

export default Supplier;
